import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DropdownModule } from 'primeng/dropdown';
import { Chart, registerables } from 'chart.js';
import {
  DashboardService,
  DashboardOverview,
  TeamMemberStats
} from '../services/dashboard.service';

Chart.register(...registerables);

interface UserOption {
  label: string;
  value: number | null;
}

const CHART_COLORS = [
  '#2563eb', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#64748b'
];

const PHASE_COLORS: { [phase: string]: string } = {
  'Nouveau LEAD / RDV': '#2563eb',
  'Echange': '#06b6d4',
  'Devis envoyé': '#f59e0b',
  'Relance & objections en cours': '#f97316',
  'Questionnaire médical envoyé': '#8b5cf6',
  'Fermé gagné': '#10b981',
  'Fermé perdu': '#dc2626',
  'EVENEMENT / AVIS / PARRAINAGE': '#ec4899'
};

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    TableModule,
    TagModule,
    DropdownModule
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit, OnDestroy {
  overview: DashboardOverview | null = null;
  teamStats: TeamMemberStats[] = [];

  isAdmin = false;
  selectedUserId: number | null = null;
  userOptions: UserOption[] = [{ label: 'Toute l\'équipe', value: null }];

  loading = true;
  loadingTeam = false;
  error: string | null = null;

  private charts: Chart[] = [];

  constructor(
    private dashboardService: DashboardService,
    private zone: NgZone,
    public router: Router
  ) {}

  ngOnInit() {
    this.isAdmin = this.checkAdminStatus();
    this.loadDashboardData();
    if (this.isAdmin) {
      this.loadTeamStats();
    }
  }

  ngOnDestroy() {
    this.destroyCharts();
  }

  loadDashboardData() {
    this.loading = true;
    this.error = null;

    this.dashboardService.getOverview(this.isAdmin ? this.selectedUserId : null).subscribe({
      next: (data) => {
        this.overview = data;
        this.loading = false;
        // Les canvas n'existent qu'après le rendu du template
        setTimeout(() => this.renderCharts(), 0);
      },
      error: (err) => {
        this.error = 'Erreur lors du chargement du tableau de bord';
        console.error(err);
        this.loading = false;
      }
    });
  }

  loadTeamStats() {
    this.loadingTeam = true;
    this.dashboardService.getTeamOverview().subscribe({
      next: (data) => {
        this.teamStats = data;
        this.userOptions = [
          { label: 'Toute l\'équipe', value: null },
          ...data.map(u => ({ label: u.name, value: u.user_id }))
        ];
        this.loadingTeam = false;
      },
      error: (err) => {
        console.error('Erreur chargement stats équipe:', err);
        this.loadingTeam = false;
      }
    });
  }

  onScopeChange() {
    this.loadDashboardData();
  }

  refresh() {
    this.loadDashboardData();
    if (this.isAdmin) {
      this.loadTeamStats();
    }
  }

  // ================= Charts =================

  private destroyCharts() {
    this.charts.forEach(c => c.destroy());
    this.charts = [];
  }

  private renderCharts() {
    this.destroyCharts();
    if (!this.overview) return;
    // Hors zone Angular : les requestAnimationFrame de Chart.js ne doivent pas
    // déclencher la détection de changements en continu
    this.zone.runOutsideAngular(() => {
      this.renderMonthlyChart();
      this.renderPipelineChart();
      this.renderOrigineChart();
      this.renderObjetChart();
      this.renderAssureursChart();
    });
  }

  private getCtx(id: string): CanvasRenderingContext2D | null {
    const canvas = document.getElementById(id) as HTMLCanvasElement | null;
    return canvas ? canvas.getContext('2d') : null;
  }

  private renderMonthlyChart() {
    const ctx = this.getCtx('monthlyChart');
    const data = this.overview?.monthly_evolution || [];
    if (!ctx || !data.length) return;

    this.charts.push(new Chart(ctx, {
      type: 'bar',
      data: {
        labels: data.map(d => d.month),
        datasets: [
          {
            type: 'bar',
            label: 'CA (€)',
            data: data.map(d => d.amount),
            backgroundColor: 'rgba(37, 99, 235, 0.65)',
            borderColor: '#2563eb',
            borderWidth: 1,
            yAxisID: 'y',
            order: 2
          },
          {
            type: 'line',
            label: 'Projets',
            data: data.map(d => d.projects),
            borderColor: '#10b981',
            backgroundColor: '#10b981',
            tension: 0.3,
            yAxisID: 'y1',
            order: 1
          },
          {
            type: 'line',
            label: 'Gagnés',
            data: data.map(d => d.won),
            borderColor: '#f59e0b',
            backgroundColor: '#f59e0b',
            borderDash: [6, 4],
            tension: 0.3,
            yAxisID: 'y1',
            order: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          y: {
            beginAtZero: true,
            position: 'left',
            ticks: {
              callback: (value) => Number(value).toLocaleString('fr-FR') + ' €'
            }
          },
          y1: {
            beginAtZero: true,
            position: 'right',
            grid: { drawOnChartArea: false },
            ticks: { precision: 0, maxTicksLimit: 8 }
          }
        }
      }
    }));
  }

  private renderPipelineChart() {
    const ctx = this.getCtx('pipelineChart');
    const phases = (this.overview?.pipeline || []).filter(p => p.count > 0);
    if (!ctx || !phases.length) return;

    this.charts.push(new Chart(ctx, {
      type: 'bar',
      data: {
        labels: phases.map(p => p.phase),
        datasets: [{
          label: 'Dossiers',
          data: phases.map(p => p.count),
          backgroundColor: phases.map(p => PHASE_COLORS[p.phase] || '#64748b')
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              afterLabel: (item) => {
                const phase = phases[item.dataIndex];
                return 'CA : ' + phase.amount.toLocaleString('fr-FR') + ' €';
              }
            }
          }
        },
        scales: {
          x: { beginAtZero: true, ticks: { precision: 0, maxTicksLimit: 10 } }
        }
      }
    }));
  }

  private renderDoughnut(canvasId: string, allItems: { label: string; count: number }[]) {
    const ctx = this.getCtx(canvasId);
    if (!ctx || !allItems.length) return;

    // Limiter à 8 segments : au-delà, la légende devient illisible et lourde à rendre
    let items = allItems;
    if (allItems.length > 8) {
      const top = allItems.slice(0, 7);
      const restCount = allItems.slice(7).reduce((sum, i) => sum + i.count, 0);
      items = [...top, { label: 'Autres', count: restCount }];
    }

    this.charts.push(new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: items.map(i => i.label),
        datasets: [{
          data: items.map(i => i.count),
          backgroundColor: items.map((_, idx) => CHART_COLORS[idx % CHART_COLORS.length]),
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right', labels: { boxWidth: 12 } }
        }
      }
    }));
  }

  private renderOrigineChart() {
    this.renderDoughnut('origineChart', this.overview?.by_origine || []);
  }

  private renderObjetChart() {
    this.renderDoughnut('objetChart', this.overview?.by_objet_pret || []);
  }

  private renderAssureursChart() {
    const ctx = this.getCtx('assureursChart');
    const items = this.overview?.top_assureurs || [];
    if (!ctx || !items.length) return;

    this.charts.push(new Chart(ctx, {
      type: 'bar',
      data: {
        labels: items.map(i => i.name),
        datasets: [{
          label: 'Contrats souscrits',
          data: items.map(i => i.projects),
          backgroundColor: '#8b5cf6',
          borderColor: '#7c3aed',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              afterLabel: (item) => {
                const a = items[item.dataIndex];
                return 'CA : ' + a.amount.toLocaleString('fr-FR') + ' €';
              }
            }
          }
        },
        scales: {
          x: { beginAtZero: true, ticks: { precision: 0, maxTicksLimit: 10 } }
        }
      }
    }));
  }

  // ================= Helpers template =================

  navigateToComparison() {
    this.router.navigate(['/cac/comparaison']);
  }

  navigateToHistory() {
    this.router.navigate(['/cac/project-history']);
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getScopeLabel(): string {
    if (!this.isAdmin) return 'Mes statistiques';
    if (this.selectedUserId === null) return 'Toute l\'équipe';
    const user = this.userOptions.find(o => o.value === this.selectedUserId);
    return user ? user.label : 'Utilisateur';
  }

  formatAmount(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  }

  formatEvolution(value: number | undefined | null): string {
    const v = value || 0;
    return (v > 0 ? '+' : '') + v.toFixed(1) + '%';
  }

  getRelanceSeverity(days: number): string {
    if (days > 14) return 'danger';
    if (days > 7) return 'warning';
    return 'info';
  }

  getPhaseSeverity(phase: string): string {
    switch (phase) {
      case 'Devis envoyé': return 'warning';
      case 'Relance & objections en cours': return 'danger';
      case 'Questionnaire médical envoyé': return 'info';
      default: return 'secondary';
    }
  }

  getConversionSeverity(rate: number): string {
    if (rate >= 50) return 'success';
    if (rate >= 25) return 'warning';
    return 'danger';
  }

  checkAdminStatus(): boolean {
    const role = localStorage.getItem('role');
    return role === 'ADMIN' || role === 'admin';
  }
}
